import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import { ResourceLocation } from "@cmp-src/data-display/resource/ResourceLocation";
import { AgendaSchedule, ISchedule } from "@gettactic/api";
import { findCheckinTargetSlot } from "@gettactic/helpers";
import { createMapLink } from "@gettactic/helpers";
import { useState } from "react";

import {
  ISlot,
  getReservationDataFromSlot
} from "@gettactic/helpers/src/reservation/getReservationDataFromSlot";
import { isPast } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { useMemo } from "react";
import {
  AgendaCardButton,
  AgendaCardLayout,
  AgendaCardLayoutProps,
  DotsMenu
} from "./AgendaCardLayout";

import { Button } from "@cmp-src/primitives/button";
import { ViewDeskIcon } from "@cmp-src/icons/TacticIcons/VIewDeskIcon";
import { ConfirmModal } from "@cmp-src/overlay/modals/ConfirmModal";
import { IResource } from "@gettactic/api";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { formatWalkUpOccupancy } from "@gettactic/helpers/src/schedules/formatWalkUpOccupancy";
import { getScheduleAttendeeUsers } from "@gettactic/helpers/src/schedules/getScheduleAttendees";
import { getScheduleRooms } from "@gettactic/helpers/src/schedules/getScheduleRooms";
import { useAuthenticated } from "@gettactic/hooks";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { useRouter } from "../../useRouter";

export type ExtendedSchedule = Omit<ISchedule, "resource"> & {
  resource: IResource & {
    location: string;
    floor: {
      id: string;
      name: string;
    };
    area: string;
  };
};

export interface CheckInCardProps {
  reservation: AgendaSchedule;
  layoutProps?: AgendaCardLayoutProps;
  /** Called on a click on the card. */
  onClick?: () => void;
  /** Called by Check In with the viewer's slot. */
  onCheckin?: (resourceId: string, scheduleId: string, slotId: string) => void;
  /** Called by Check out, after its confirmation, with the viewer's slot. */
  onCheckout?: (resourceId: string, scheduleId: string, slotId: string) => void;
  onDuplicate?: () => void;
  onExtend?: () => void;
  /** Disables the buttons while a check-in or check-out runs. */
  loading?: boolean;
  /** Shows Check In; by default until the viewer has checked in. */
  showCheckInButton?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
}

/**
 * The agenda card of a booking: times, place and location, the dots menu and Check In or
 * Check out for the viewer's slot.
 */
export const CheckInCard = ({
  reservation,
  layoutProps,
  onClick,
  onCheckin,
  onCheckout,
  loading = false,
  showCheckInButton: showCheckInButtonProp,
  onEdit,
  onCancel,
  onDuplicate,
  onExtend
}: CheckInCardProps) => {
  const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
  const areas = reservation.resource.areas || [];

  const router = useRouter();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const officeId = reservation.resource.office_id;
  const parentId = reservation.resource.parent_id;
  const officeName =
    authenticatedUser.offices?.byId[
      officeId as keyof typeof authenticatedUser.offices.byId
    ]?.name;
  const areaName = parentId
    ? areas.find((area) => area.id === parentId)?.name
    : undefined;

  const tz = reservation.office?.time_zone ?? reservation.time_zone;

  const start = useMemo(() => {
    return formatInTimeZone(reservation.start, tz, "h:mm a");
  }, [reservation.start, tz]);

  // Walk-up check-ins have no end time until checkout.
  const end = useMemo(() => {
    return reservation.end
      ? formatInTimeZone(reservation.end, tz, "h:mm a")
      : null;
  }, [reservation.end, tz]);

  // `check_in_time` is a real UTC instant (unlike `start`/`end`, which
  // carry a trailing `Z` but are actually wall-clock) — this card is shown
  // on shared/kiosk devices, so it's converted to the office's own
  // timezone rather than the device's, matching `formatInTimeZone` above.
  const walkUpCheckInTimeStr = reservation.slots.find(
    (x) => x.check_in_time
  )?.check_in_time;
  const walkUpCheckInTime = walkUpCheckInTimeStr
    ? toZonedTime(new Date(walkUpCheckInTimeStr), tz)
    : null;
  // "Today" must be the office's today, not the kiosk device's — computed
  // the same way as walkUpCheckInTime so the two compare fairly.
  const walkUpNowInOfficeTime = toZonedTime(new Date(), tz);

  const timeRangeLabel =
    reservation.resource.is_walkup && !reservation.end
      ? formatWalkUpOccupancy(walkUpCheckInTime, walkUpNowInOfficeTime)
      : `${start} - ${end}`;

  const targetSlot = findCheckinTargetSlot(
    reservation,
    authenticatedUser.user?.id ?? ""
  );

  const {
    allowCheckIn,
    allowCheckOut: allowCheckOutFromSlot,
    canEdit,
    canRespondCancel
  } = getReservationDataFromSlot(targetSlot as unknown as ISlot);
  // Walk-up occupancy is checked out externally via the API, never through
  // the app.
  const allowCheckOut = allowCheckOutFromSlot && !reservation.resource.is_walkup;

  const handleCheckInClick = () => {
    if (!onCheckin) return;
    if (!targetSlot) return;
    onCheckin(targetSlot.resource.id, reservation.id, targetSlot.id);
  };

  const handleCheckOutClick = () => {
    if (!onCheckout) return;
    if (!targetSlot) return;
    onCheckout(targetSlot.resource.id, reservation.id, targetSlot.id);
    setIsCheckOutModalOpen(false);
  };

  const _showCheckInButton =
    showCheckInButtonProp !== undefined
      ? showCheckInButtonProp
      : !reservation.has_checked_in;

  // Walk-up check-ins have no end time until checkout — never "in the past".
  const scheduleInThePast = !!reservation.end && isPast(new Date(reservation.end));

  const floor = reservation.resource.floor;

  const link = createMapLink({
    date: dateStringToLocalTime(reservation.start),
    officeId: reservation.resource.office_id,
    resourceId: reservation.resource?.id,
    floorId: floor?.id ?? undefined,
    startTime: dateStringToLocalTime(reservation.start),
    endTime: reservation.end
      ? dateStringToLocalTime(reservation.end)
      : undefined
  });

  const formattedCheckOutTime = targetSlot?.check_out_time
    ? formatInTimeZone(targetSlot.check_out_time, tz, "HH:mm")
    : null;

  const isMeetingRoom = reservation.resource.type === "meeting_room";

  // A reservation holding several rooms is the event, not one of its rooms: it leads with
  // the event name, and falls back to the room count when the booking has no title.
  const rooms = getScheduleRooms(reservation);
  const isMultiRoom = rooms.length > 1;
  const cardTitle = isMultiRoom
    ? reservation.title || `${rooms.length} Rooms`
    : reservation.resource.name;

  return (
    <AgendaCardLayout
      onCardClick={onClick}
      topLeft={
        <div className="flex items-center font-inter">
          {timeRangeLabel}{" "}
          {targetSlot?.check_out_time ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <span className="ml-1 text-content text-sm">
                    ({formattedCheckOutTime})
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Checked out at {formattedCheckOutTime}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
      }
      dotsMenu={
        (canEdit || canRespondCancel) && (
          <DotsMenu
            onManage={onEdit}
            onRemove={onCancel}
            canManage={canEdit}
            canRemove={canRespondCancel}
            onlineMeeting={reservation.online_meeting}
            onDuplicate={onDuplicate}
            onExtend={onExtend}
            canExtend={
              reservation.has_checked_in &&
              !reservation.has_checked_out &&
              !scheduleInThePast &&
              !!onExtend
            }
          />
        )
      }
      bottomLeft={
        <div className="flex flex-col space-y-1">
          <Button
            variant="ghost"
            className="group/resource !px-0 !py-0 flex flex-row items-center font-neutral-900 !h-auto hover:!bg-transparent"
            labelClassName="!justify-start"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              router.push(link);
            }}
          >
            <div className="max-w-[250px] truncate text-left text-neutral-1000 font-inter font-bold">
              {cardTitle}{" "}
            </div>
            <div className="!px-0 !py-0 ml-0.5 hidden flex items-center font-bold group-hover/resource:block">
              <ViewDeskIcon
                className=" h-4 w-4 "
                pathProps={{
                  fill: "hsl(var(--primary))"
                }}
              />
            </div>
          </Button>
          <ResourceLocation
            location={officeName}
            floor={floor.name}
            area={areaName}
            className="text-content"
            maxLength={isMeetingRoom ? 37 : 53}
          />
        </div>
      }
      bottomRight={
        isMeetingRoom ? (
          <div className="flex items-end">
            <UserAvatarStack users={getScheduleAttendeeUsers(reservation)} />
          </div>
        ) : null
      }
      button={
        (allowCheckIn && _showCheckInButton) || allowCheckOut ? (
          <>
            {allowCheckIn && _showCheckInButton && (
              <AgendaCardButton onClick={handleCheckInClick} disabled={loading}>
                {loading ? "Processing..." : "Check In"}
              </AgendaCardButton>
            )}
            {allowCheckOut && (
              <AgendaCardButton
                onClick={() => setIsCheckOutModalOpen(true)}
                disabled={loading}
                variant="secondary"
              >
                {loading ? "Processing..." : "Check out"}
              </AgendaCardButton>
            )}
            {isCheckOutModalOpen && (
              <ConfirmModal
                title="Check out"
                message="Are you sure you want to check out?"
                onConfirm={handleCheckOutClick}
                onOpenChange={setIsCheckOutModalOpen}
                onCancel={() => setIsCheckOutModalOpen(false)}
                isOpen={isCheckOutModalOpen}
              />
            )}
          </>
        ) : null
      }
      hasHover={!!reservation}
      {...layoutProps}
    />
  );
};
